// -----------------------------------------------------------------------------
// VoiceConsole.jsx
// ChatGPT-style floating bar voice interface for Harbour Nest
//
// DESIGN:
// - Bottom-center floating bar
// - Input field + microphone button
// - Expands to show transcript and response
// - Brand-aligned (white / #CC0202)
// -----------------------------------------------------------------------------

import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import useVoiceAssistant from "./useVoiceAssistant.js";
import { Mic, Send, X, Trash2, MessageCircle, ChevronDown } from "lucide-react";

const AUTH_PATHS = ['/onboarding', '/login', '/register', '/forgot-password', '/reset-password'];

export default function VoiceConsole() {
  const location = useLocation();
  const {
    status,
    transcript,
    error,
    conversationHistory,
    startListening,
    stopListening,
    sendTextQuery,
    clearConversation
  } = useVoiceAssistant();

  const [textInput, setTextInput] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMinimized, setIsMinimized] = useState(true);
  const messagesEndRef = React.useRef(null);

  const isListening = status === "listening";
  const isProcessing = status === "processing";
  const hasContent = conversationHistory.length > 0 || transcript || error;

  // Expand when there's content or user is listening
  const shouldExpand = isExpanded || hasContent || isListening;

  // Auto-scroll to bottom when new messages arrive
  React.useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversationHistory, transcript]);

  if (AUTH_PATHS.some(p => location.pathname.startsWith(p))) return null;

  const handleSend = async () => {
    if (!textInput.trim()) return;

    setIsExpanded(true);
    await sendTextQuery(textInput);
    setTextInput("");
  };

  const handleMicClick = () => {
    if (isListening) {
      stopListening();
    } else {
      setIsExpanded(true);
      startListening();
    }
  };

  const handleClose = () => {
    setIsExpanded(false);
    setTextInput("");
  };

  const handleClearConversation = () => {
    clearConversation();
    setTextInput("");
  };

  // When minimized, show a small floating button in the bottom-right
  if (isMinimized) {
    return (
      <button
        onClick={() => setIsMinimized(false)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#CC0202] text-white shadow-2xl flex items-center justify-center hover:bg-red-700 transition-all hover:scale-105"
        title="Open Harbour Nest Assistant"
      >
        <MessageCircle className="w-6 h-6" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 flex flex-col items-center pointer-events-none">

      {/* EXPANDED RESPONSE PANEL */}
      {shouldExpand && (
        <div className="w-full max-w-3xl mb-4 mx-4 pointer-events-auto">
          <div className="bg-white rounded-2xl shadow-2xl border-2 border-gray-200 overflow-hidden">

            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${
                  isListening ? 'bg-red-500 animate-pulse' :
                  isProcessing ? 'bg-amber-500 animate-pulse' :
                  'bg-green-500'
                }`} />
                <span className="text-sm font-semibold text-gray-700">
                  {isListening ? 'Listening...' :
                   isProcessing ? 'Processing...' :
                   'Harbour Nest Assistant'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {conversationHistory.length > 0 && (
                  <button
                    onClick={handleClearConversation}
                    className="text-gray-400 hover:text-red-600 transition"
                    title="Clear conversation"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => setIsMinimized(true)}
                  className="text-gray-400 hover:text-gray-600 transition"
                  title="Collapse to corner"
                >
                  <ChevronDown className="w-5 h-5" />
                </button>
                <button
                  onClick={handleClose}
                  className="text-gray-400 hover:text-gray-600 transition"
                  title="Close chat"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content Area - Conversation History */}
            <div className="p-4 max-h-96 overflow-y-auto space-y-3">

              {/* Error */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* Conversation History */}
              {conversationHistory.length > 0 && conversationHistory.map((message, index) => (
                <div
                  key={index}
                  className={`rounded-lg p-3 ${
                    message.role === 'user'
                      ? 'bg-blue-50 border border-blue-200 ml-8'
                      : 'bg-emerald-50 border border-emerald-200 mr-8'
                  }`}
                >
                  <p className={`text-xs font-semibold mb-1 ${
                    message.role === 'user' ? 'text-blue-900' : 'text-emerald-900'
                  }`}>
                    {message.role === 'user' ? 'You' : 'Harbour Nest'}
                  </p>
                  <p className={`text-sm whitespace-pre-wrap ${
                    message.role === 'user' ? 'text-blue-800' : 'text-emerald-800'
                  }`}>
                    {message.content}
                  </p>
                </div>
              ))}

              {/* Current Transcript (while listening/processing) */}
              {transcript && !isProcessing && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 ml-8 opacity-75">
                  <p className="text-xs font-semibold text-blue-900 mb-1">You</p>
                  <p className="text-sm text-blue-800">{transcript}</p>
                </div>
              )}

              {/* Listening indicator */}
              {isListening && !transcript && (
                <div className="flex items-center justify-center py-8">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }} />
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }} />
                  </div>
                </div>
              )}

              {/* Processing indicator */}
              {isProcessing && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mr-8">
                  <p className="text-xs font-semibold text-emerald-900 mb-1">Harbour Nest</p>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                    <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }} />
                    <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }} />
                  </div>
                </div>
              )}

              {/* Empty state */}
              {conversationHistory.length === 0 && !isListening && !transcript && !error && (
                <div className="text-center py-8 text-gray-400">
                  <p className="text-sm">Start a conversation by typing or using your microphone</p>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>
        </div>
      )}

      {/* BOTTOM INPUT BAR */}
      <div className="w-full max-w-3xl mb-6 mx-4 pointer-events-auto">
        <div className="bg-white rounded-full shadow-2xl border-2 border-gray-200 flex items-center gap-3 px-4 py-3">

          {/* Text Input */}
          <input
            type="text"
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
            placeholder="Ask Harbour Nest anything..."
            className="flex-1 bg-transparent border-none outline-none text-gray-800 text-sm placeholder-gray-400"
            disabled={isListening || isProcessing}
          />

          {/* Send Button */}
          {textInput && (
            <button
              onClick={handleSend}
              disabled={isProcessing}
              className="w-10 h-10 rounded-full bg-hn-red text-white flex items-center justify-center hover:bg-red-700 transition disabled:opacity-50"
            >
              <Send className="w-5 h-5" />
            </button>
          )}

          {/* Microphone Button */}
          <button
            onClick={handleMicClick}
            disabled={isProcessing}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition ${
              isListening
                ? 'bg-red-500 text-white animate-pulse'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            } disabled:opacity-50`}
          >
            <Mic className="w-5 h-5" />
          </button>

          {/* Minimize Button */}
          <button
            onClick={() => setIsMinimized(true)}
            className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-100 text-gray-700 hover:bg-gray-200 transition"
            title="Collapse to corner"
          >
            <ChevronDown className="w-5 h-5" />
          </button>

        </div>

        {/* Hint text */}
        <p className="text-xs text-gray-400 text-center mt-2">
          {isListening ? 'Speak now... Click mic again to stop' : 'Type or click microphone to speak'}
        </p>
      </div>

    </div>
  );
}
